// =============================================================================
// configure-storage.ts — localStorage persistence for the Configure Solution
// modal. Retains the POC `configSelectionsByProduct` key and base item shape,
// extended with CR-006 user overrides and workspace-local manual quote lines.
//
// Disk shapes supported:
//   - MVP object map: { [productId]: { items, optionsTotal?, ... } }
//   - POC array: [{ productId, items, optionsTotal?, ... }, ...]
//
// Reads are defensive — corrupted JSON resolves to "no saved selection".
// Writes use the MVP object-map shape.
// =============================================================================

import { parseDecimalString } from "@/utils/cost-display";

const STORAGE_KEY = "configSelectionsByProduct";

/** POC `CUSTOM_TOOLING_PART_NO` — synthetic row in the Tooling section. */
export const CUSTOM_TOOLING_PART_NO = "__CUSTOM_TOOLING__";

export interface ConfigureSelectionItem {
  partNo: string;
  qty: number;
  /** Persisted line price when prices are known at save time. */
  price?: number;
}

/**
 * A user-authored change layered over the current system recommendation.
 * `qty = 0` is the tombstone that keeps a recommended row unchecked.
 */
export interface ConfigureSelectionOverride {
  partNo: string;
  qty: number;
}

/** Ad-hoc quote line retained for the current Selector workspace. */
export interface ConfigureManualLineItem {
  description: string;
  price: string;
  qty: number;
  currency: string;
  partNo?: string | null;
  category?: string | null;
  itemName?: string | null;
}

function normalizeCurrencyCode(currency: string): string {
  return currency.trim().toUpperCase();
}

/** Manual quote lines visible for the active regional currency only. */
export function manualLineItemsForCurrency(
  items: ReadonlyArray<ConfigureManualLineItem> | undefined,
  currency: string,
): ConfigureManualLineItem[] {
  const activeCurrency = normalizeCurrencyCode(currency);
  return (items ?? []).filter(
    (item) => normalizeCurrencyCode(item.currency) === activeCurrency,
  );
}

/** Display-only subtotal for manual quote rows in the active currency. */
export function manualLineItemsTotalForCurrency(
  items: ReadonlyArray<ConfigureManualLineItem> | undefined,
  currency: string,
): number {
  let total = 0;
  for (const item of manualLineItemsForCurrency(items, currency)) {
    const unitPrice = parseDecimalString(item.price);
    if (unitPrice == null || unitPrice < 0) continue;
    const qty = Number.isFinite(item.qty) && item.qty > 0 ? item.qty : 1;
    total += unitPrice * qty;
  }
  return total;
}

/**
 * Replace one currency bucket without discarding hidden regional lines.
 * Removing every visible line therefore clears only the active currency.
 */
export function replaceManualLineItemsForCurrency(
  persistedItems: ReadonlyArray<ConfigureManualLineItem> | undefined,
  currency: string,
  activeItems: ReadonlyArray<ConfigureManualLineItem>,
): ConfigureManualLineItem[] {
  const activeCurrency = normalizeCurrencyCode(currency);
  const hiddenItems = (persistedItems ?? []).filter(
    (item) => normalizeCurrencyCode(item.currency) !== activeCurrency,
  );
  return [
    ...hiddenItems,
    ...activeItems.filter(
      (item) => normalizeCurrencyCode(item.currency) === activeCurrency,
    ),
  ];
}

export interface ConfigureProductSelection {
  items: ConfigureSelectionItem[];
  /** Explicit checkbox / quantity edits; applied after recommendations. */
  userOverrides?: ConfigureSelectionOverride[];
  /** Frozen task snapshots are exact, not recommendation overlays. */
  selectionFrozen?: boolean;
  /** Non-task manual lines are quote-only, but survive close/reopen. */
  manualLineItems?: ConfigureManualLineItem[];
  /** POC `optionsTotal` — sum of selected line prices × qty. */
  optionsTotal?: number;
  customToolingText?: string;
  profilElementType?: string;
  mdsFastenerGroup?: string;
  mdsFeederTracks?: string;
  mdsForceKn?: string;
}

type StoredMap = Record<string, ConfigureProductSelection>;

function normalizeEntry(raw: unknown): ConfigureProductSelection | null {
  if (!raw || typeof raw !== "object") return null;
  const entry = raw as Record<string, unknown>;
  const itemsRaw = entry.items;
  if (!Array.isArray(itemsRaw)) return null;

  const items = itemsRaw
    .filter(
      (it): it is ConfigureSelectionItem =>
        !!it &&
        typeof it === "object" &&
        typeof (it as { partNo?: unknown }).partNo === "string" &&
        Number.isFinite((it as { qty?: unknown }).qty),
    )
    .map((it) => {
      const row: ConfigureSelectionItem = {
        partNo: String((it as ConfigureSelectionItem).partNo),
        qty: Math.max(0, Number((it as ConfigureSelectionItem).qty)),
      };
      const p = (it as { price?: unknown }).price;
      if (typeof p === "number" && Number.isFinite(p) && p >= 0) row.price = p;
      return row;
    });

  const optionsTotalRaw = entry.optionsTotal;
  const optionsTotal =
    typeof optionsTotalRaw === "number" &&
    Number.isFinite(optionsTotalRaw) &&
    optionsTotalRaw > 0
      ? optionsTotalRaw
      : undefined;

  const userOverrides = Array.isArray(entry.userOverrides)
    ? entry.userOverrides
        .filter(
          (it): it is ConfigureSelectionOverride =>
            !!it &&
            typeof it === "object" &&
            typeof (it as { partNo?: unknown }).partNo === "string" &&
            Number.isFinite((it as { qty?: unknown }).qty),
        )
        .map((it) => ({
          partNo: String(it.partNo),
          qty: Math.max(0, Math.floor(Number(it.qty))),
        }))
    : [];

  const manualLineItems = Array.isArray(entry.manualLineItems)
    ? entry.manualLineItems
        .filter(
          (it): it is ConfigureManualLineItem =>
            !!it &&
            typeof it === "object" &&
            typeof (it as { description?: unknown }).description === "string" &&
            typeof (it as { price?: unknown }).price === "string" &&
            typeof (it as { currency?: unknown }).currency === "string" &&
            Number.isFinite((it as { qty?: unknown }).qty),
        )
        .map((it) => ({
          description: String(it.description),
          price: String(it.price),
          qty: Math.max(1, Math.floor(Number(it.qty))),
          currency: String(it.currency),
          ...(typeof it.partNo === "string" || it.partNo === null
            ? { partNo: it.partNo }
            : {}),
          ...(typeof it.category === "string" || it.category === null
            ? { category: it.category }
            : {}),
          ...(typeof it.itemName === "string" || it.itemName === null
            ? { itemName: it.itemName }
            : {}),
        }))
    : [];

  return {
    items,
    ...(userOverrides.length > 0 ? { userOverrides } : {}),
    ...(entry.selectionFrozen === true ? { selectionFrozen: true } : {}),
    ...(manualLineItems.length > 0 ? { manualLineItems } : {}),
    ...(optionsTotal !== undefined ? { optionsTotal } : {}),
    ...(typeof entry.customToolingText === "string" && entry.customToolingText
      ? { customToolingText: entry.customToolingText }
      : {}),
    ...(typeof entry.profilElementType === "string" && entry.profilElementType
      ? { profilElementType: entry.profilElementType }
      : {}),
    ...(typeof entry.mdsFastenerGroup === "string" && entry.mdsFastenerGroup
      ? { mdsFastenerGroup: entry.mdsFastenerGroup }
      : {}),
    ...(typeof entry.mdsFeederTracks === "string" && entry.mdsFeederTracks
      ? { mdsFeederTracks: entry.mdsFeederTracks }
      : {}),
    ...(typeof entry.mdsForceKn === "string" && entry.mdsForceKn
      ? { mdsForceKn: entry.mdsForceKn }
      : {}),
  };
}

function readMap(): StoredMap {
  if (typeof window === "undefined" || !window.localStorage) return {};
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};

    // POC: JSON array of { productId, items, optionsTotal, ... }
    if (Array.isArray(parsed)) {
      const map: StoredMap = {};
      for (const entry of parsed) {
        const norm = normalizeEntry(entry);
        const pid =
          entry &&
          typeof entry === "object" &&
          typeof (entry as { productId?: unknown }).productId === "string"
            ? String((entry as { productId: string }).productId)
            : null;
        if (pid && norm) map[pid] = norm;
      }
      return map;
    }

    const map: StoredMap = {};
    for (const [key, entry] of Object.entries(parsed)) {
      const norm = normalizeEntry(entry);
      if (norm) map[key] = norm;
    }
    return map;
  } catch {
    return {};
  }
}

function writeMap(map: StoredMap): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Quota exceeded / SecurityError — best-effort persistence.
  }
}

export function loadProductSelection(
  productId: string,
): ConfigureProductSelection | null {
  const map = readMap();
  return map[productId] ?? null;
}

export function saveProductSelection(
  productId: string,
  selection: ConfigureProductSelection,
): void {
  const map = readMap();
  map[productId] = selection;
  writeMap(map);
}

export function clearProductSelection(productId: string): void {
  const map = readMap();
  if (productId in map) {
    delete map[productId];
    writeMap(map);
  }
}

/**
 * Drop every saved configuration. Called on page load and after an application
 * is created/edited so the Configure popup always renders the machine options
 * the API currently recommends, never a stale selection made against an older
 * application or catalog.
 */
export function clearAllProductSelections(): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // SecurityError (blocked storage) — nothing to clear.
  }
}

/**
 * Convert a `Map<partNo, qty>` into the POC disk shape, optionally attaching
 * unit prices for configured-investment override on compare cards.
 */
export function selectionMapToItems(
  map: ReadonlyMap<string, number>,
  unitPriceByPartNo?: ReadonlyMap<string, number>,
): ConfigureSelectionItem[] {
  const out: ConfigureSelectionItem[] = [];
  for (const [partNo, qty] of map) {
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const row: ConfigureSelectionItem = { partNo, qty };
    const price = unitPriceByPartNo?.get(partNo);
    if (price != null && Number.isFinite(price) && price >= 0)
      row.price = price;
    out.push(row);
  }
  return out;
}

export function itemsToSelectionMap(
  items: ReadonlyArray<ConfigureSelectionItem>,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const it of items) {
    if (Number.isFinite(it.qty) && it.qty > 0) map.set(it.partNo, it.qty);
  }
  return map;
}

/** Convert persisted overrides (including qty=0 tombstones) to a live map. */
export function overridesToSelectionMap(
  overrides: ReadonlyArray<ConfigureSelectionOverride> | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const override of overrides ?? []) {
    if (!override.partNo || !Number.isFinite(override.qty)) continue;
    map.set(override.partNo, Math.max(0, Math.floor(override.qty)));
  }
  return map;
}

/**
 * CR-006 precedence: recommendations establish untouched rows; explicit user
 * edits win last. Invalid part numbers are ignored when a catalog is supplied.
 */
export function applyUserSelectionOverrides(
  recommended: ReadonlyMap<string, number>,
  overrides: ReadonlyMap<string, number>,
  allowedPartNos?: ReadonlySet<string>,
): Map<string, number> {
  const resolved = new Map(recommended);
  for (const [partNo, qty] of overrides) {
    if (allowedPartNos && !allowedPartNos.has(partNo)) continue;
    if (!Number.isFinite(qty) || qty <= 0) resolved.delete(partNo);
    else resolved.set(partNo, Math.max(1, Math.floor(qty)));
  }
  return resolved;
}

/** Persist a live override map without dropping qty=0 deselection tombstones. */
export function selectionOverridesToItems(
  overrides: ReadonlyMap<string, number>,
  allowedPartNos?: ReadonlySet<string>,
): ConfigureSelectionOverride[] {
  const out: ConfigureSelectionOverride[] = [];
  for (const [partNo, qty] of overrides) {
    if (!partNo || !Number.isFinite(qty)) continue;
    if (allowedPartNos && !allowedPartNos.has(partNo)) continue;
    out.push({ partNo, qty: Math.max(0, Math.floor(qty)) });
  }
  return out;
}
